/** @jsx jsx */

import React, { FC, Fragment as _, useState, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { colors } from 'styles/variables';
import EditorBtn from 'components/UI/EditorBtn';
import Modal from 'components/UI/Modal';
import Spinner from 'components/UI/LoadingSpinner';
import Tick from 'assets/assessment_correct.svg';

const { mint } = colors;

const URL_PREFIX = window['chameleon'].CHAMELEON_URL;

interface IAudioUploadStatusProps {
  uploadStatus: {
    uploading: boolean;
    processing: boolean;
    ready: boolean;
  };
  onCancel: () => void;
  onClose: () => void;
}

interface IStatusProps {
  label: string;
  active: boolean;
  children: any;
}

const Status = ({ label, children, active = false }: IStatusProps) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: 200,
        height: 30,
      }}
    >
      <div
        style={{
          opacity: active ? 1 : 0.2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: 15,
            height: 15,
            borderRadius: '50%',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const AudioUploadStatus: FC<IAudioUploadStatusProps> = props => {
  const { uploadStatus, onCancel, onClose } = props as any;
  const { uploading, progress = 0, processing, ready, error } = uploadStatus;

  return (
    <Modal
      minWidth="320px"
      onClose={() => {
        if (error) {
          onClose();
        }

        if (!ready) {
          return;
        }

        onClose();
      }}
    >
      <div
        style={{
          padding: error ? 100 : 50,
        }}
      >
        {error && (
          <_>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              Whoops! Something went wrong. Please try again.
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '30px 0 0 0',
              }}
            >
              <EditorBtn
                label="Close"
                style={{
                  background: mint,
                  minWidth: 200,
                  height: 40,
                }}
                onClick={onClose}
              />
            </div>
          </_>
        )}

        {!error && (
          <_>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 70,
                marginBottom: 20,
                fontSize: 20,
              }}
            >
              {!ready
                ? 'Preparing your mp3. If this is a large file it could take a few minutes.'
                : 'Your mp3 has been uploaded successfully.'}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Status label="Uploading" active={uploading}>
                {progress === 100 ? (
                  <img className="fader_-5s" src={Tick} style={{}} />
                ) : (
                  <div
                    style={{ color: mint, fontWeight: 500, textAlign: 'right' }}
                  >
                    {progress}%
                  </div>
                )}
              </Status>

              <Status label="Processing" active={processing}>
                {processing && !ready && (
                  <Spinner size={15} color={mint} lineWeight={2} />
                )}
                {ready && <img className="fader_-5s" src={Tick} style={{}} />}
              </Status>

              <Status label="Ready" active={ready}>
                {ready ? (
                  <img className="fader_-5s" src={Tick} style={{}} />
                ) : null}
              </Status>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '30px 0 30px 0',
                // visibility: ready ? 'visible' : 'hidden',
              }}
            >
              <EditorBtn
                label={ready ? 'Continue' : 'Cancel'}
                style={{
                  background: mint,
                  minWidth: 200,
                  height: 40,
                }}
                onClick={() => {
                  if (!ready) {
                    // console.log('should cancel upload and bail');
                    onCancel();
                  }

                  onClose();
                }}
              />
            </div>
          </_>
        )}
      </div>
    </Modal>
  );
};

const AudioUploadStatusWithProgress = props => {
  const { uploadStatus, onCancel, onClose } = props as any;

  const {
    progress,
    uploading,
    processing,
    ready = false,
    error = false,
  } = uploadStatus;

  return (
    <Modal
      minWidth="320px"
      onClose={() => {
        if (error) {
          onClose();
        }

        if (!ready) {
          return;
        }

        onClose();
      }}
    >
      <div
        style={{
          padding: error ? 100 : 50,
        }}
      >
        {error && (
          <_>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              Whoops! Something went wrong. Please try again.
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '30px 0 0 0',
              }}
            >
              <EditorBtn
                label="Close"
                style={{
                  background: mint,
                  minWidth: 200,
                  height: 40,
                }}
                onClick={onClose}
              />
            </div>
          </_>
        )}

        {!error && (
          <_>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 70,
                marginBottom: 20,
                fontSize: 20,
              }}
            >
              {!ready
                ? 'Preparing your document. If this is a large file it could take a few minutes.'
                : 'Your document has been uploaded successfully.'}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                css={css({
                  width: '100%',
                  maxWidth: 320,
                  height: 15,
                  margin: '0px 0 0 0',
                  borderRadius: 9999,
                  overflow: 'hidden',
                  background: '#b3f4f2',
                })}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    height: 'inherit',
                    borderRadius: 9999,
                    background: colors.lTeal1,
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '30px 0 30px 0',
                visibility: ready ? 'visible' : 'hidden',
              }}
            >
              <EditorBtn
                label="Continue"
                style={{
                  background: mint,
                  minWidth: 200,
                }}
                onClick={onClose}
              />
            </div>
          </_>
        )}
      </div>
    </Modal>
  );
};

export { AudioUploadStatus, AudioUploadStatusWithProgress };
